import React from "react";
import { useConfig } from "../lib/useConfig";
import { useLang } from "../lib/i18n";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { RotateCcw } from "lucide-react";

const ALL = "__all__";

export default function GlobalFilters({ value, onChange, showEpi = false, epis = [] }) {
  const { data: cfg } = useConfig();
  const { t } = useLang();
  const geo = cfg?.geo || {};
  const provinces = Object.keys(geo);
  const territories = value.province ? Object.keys(geo[value.province]?.territories || {}) : [];
  const zones = [];
  if (value.province) {
    const terrs = geo[value.province]?.territories || {};
    Object.entries(terrs).forEach(([t, zs]) => {
      if (!value.territory || value.territory === t) zs.forEach((z) => zones.push(z));
    });
  }

  const set = (k, v) => {
    const nv = { ...value, [k]: v === ALL ? "" : v };
    if (k === "province") { nv.territory = ""; nv.health_zone = ""; }
    if (k === "territory") nv.health_zone = "";
    onChange(nv);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 mb-6 shadow-sm">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
        <div>
          <Label className="text-xs text-slate-500">{t("Province")}</Label>
          <Select value={value.province || ALL} onValueChange={(v) => set("province", v)}>
            <SelectTrigger data-testid="filter-province" className="h-9"><SelectValue placeholder={t("Toutes")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("Toutes")}</SelectItem>
              {provinces.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-500">{t("Territoire")}</Label>
          <Select value={value.territory || ALL} onValueChange={(v) => set("territory", v)} disabled={!value.province}>
            <SelectTrigger data-testid="filter-territory" className="h-9"><SelectValue placeholder={t("Tous")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("Tous")}</SelectItem>
              {territories.map((t2) => <SelectItem key={t2} value={t2}>{t2}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-500">{t("Zone de Santé")}</Label>
          <Select value={value.health_zone || ALL} onValueChange={(v) => set("health_zone", v)} disabled={!value.province}>
            <SelectTrigger data-testid="filter-zone" className="h-9"><SelectValue placeholder={t("Toutes")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("Toutes")}</SelectItem>
              {zones.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {showEpi && (
          <div>
            <Label className="text-xs text-slate-500">{t("Épidémiologiste")}</Label>
            <Select value={value.epidemiologist_id || ALL} onValueChange={(v) => set("epidemiologist_id", v)}>
              <SelectTrigger data-testid="filter-epi" className="h-9"><SelectValue placeholder={t("Tous")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t("Tous")}</SelectItem>
                {epis.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label className="text-xs text-slate-500">{t("Du")}</Label>
          <Input type="date" value={value.date_from || ""} onChange={(e) => set("date_from", e.target.value)} data-testid="filter-from" className="h-9" />
        </div>
        <div>
          <Label className="text-xs text-slate-500">{t("Au")}</Label>
          <Input type="date" value={value.date_to || ""} onChange={(e) => set("date_to", e.target.value)} data-testid="filter-to" className="h-9" />
        </div>
      </div>
      <div className="flex justify-end mt-3">
        <Button variant="ghost" size="sm" onClick={() => onChange({})} data-testid="filter-reset" className="text-slate-500">
          <RotateCcw className="h-4 w-4 mr-1" /> {t("Réinitialiser")}
        </Button>
      </div>
    </div>
  );
}
